import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ROUTES } from "../../constants";
import { useAuth } from "../../context/authContext";
import { useCompliance } from "../../context/complianceContext";
import { toast } from "../../context/ToastContext";
import { useOptions } from "../../context/optionContext";
import { acceptCookiePolicy } from "../../services/complianceService";
import ComplianceDescriptionModal from "./ComplianceDescriptionModal";
import { openComplianceDocument } from "./complianceUtils";

const PUBLIC_ROUTES = [
	ROUTES.LOGIN,
	ROUTES.REGISTER,
	ROUTES.FORGOT_PASSWORD,
	ROUTES.RESET_PASSWORD,
	ROUTES.VERIFY_EMAIL,
	ROUTES.LOGIN_OTP,
	ROUTES.ACCEPT_EULA,
	ROUTES.LOGOUT,
];

const parseBoolean = (value) => {
	if (typeof value === "boolean") return value;
	if (typeof value === "number") return value === 1;
	const normalized = String(value || "").trim().toLowerCase();
	return ["true", "1", "yes", "y"].includes(normalized);
};

const resolveRepromptDays = (rawValue) => {
	const parsed = Number.parseInt(rawValue, 10);
	if (Number.isFinite(parsed) && parsed > 0) return parsed;
	return 30;
};

const CookieConsentBanner = () => {
	const { t } = useTranslation();
	const location = useLocation();
	const { isAuthenticated, user, updateUserStatus } = useAuth();
	const { items } = useCompliance();
	const { optionKeys } = useOptions();
	const [dismissed, setDismissed] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [policyModal, setPolicyModal] = useState({ open: false, doc: null });

	const cookieDocument = items?.COOKIE || null;

	useEffect(() => {
		setDismissed(false);
	}, [user?.user_id]);

	const isPublicRoute = useMemo(
		() => PUBLIC_ROUTES.some((route) => location.pathname.startsWith(route)),
		[location.pathname]
	);

	const repromptDays = useMemo(
		() => resolveRepromptDays(optionKeys?.cookieConsentRePromptDays),
		[optionKeys?.cookieConsentRePromptDays]
	);

	const consentDue = useMemo(() => {
		if (!user) return false;
		const isAccepted = parseBoolean(
			user.cookie_policy_accepted ?? user.user_meta?.cookie_policy_accepted
		);
		if (!isAccepted) return true;

		const acceptedDateRaw =
			user.cookie_policy_accepted_date ?? user.user_meta?.cookie_policy_accepted_date;
		if (!acceptedDateRaw) return true;

		const acceptedDate = new Date(acceptedDateRaw);
		if (Number.isNaN(acceptedDate.getTime())) return true;

		const threshold = repromptDays * 24 * 60 * 60 * 1000;
		return Date.now() - acceptedDate.getTime() >= threshold;
	}, [user, repromptDays]);

	const shouldShow =
		isAuthenticated && !isPublicRoute && !dismissed && consentDue;

	const handleViewPolicy = () => {
		openComplianceDocument({
			document: cookieDocument,
			fallbackUrl: "",
			onShowDescription: (document) => setPolicyModal({ open: true, doc: document }),
		});
	};

	const handleAccept = async () => {
		if (!user?.user_id) return;
		setSubmitting(true);
		try {
			const response = await acceptCookiePolicy(user.user_id, true, cookieDocument?.id || null);
			if (!response?.status) {
				throw new Error(response?.message || "Failed to accept cookie policy");
			}
			const acceptedAt = new Date().toISOString();
			updateUserStatus({
				cookie_policy_accepted: true,
				cookie_policy_accepted_date: acceptedAt,
				cookie_policy_document_id: cookieDocument?.id || null,
				user_meta: {
					cookie_policy_accepted: true,
					cookie_policy_accepted_date: acceptedAt,
					cookie_policy_document_id: cookieDocument?.id || "",
				},
			});
			setDismissed(true);
			toast.trigger({
				message: t("ALERT_MESSAGES.SAVED_SUCCESSFULLY", { defaultValue: "Saved successfully" }),
				severity: "success",
			});
		} catch (error) {
			toast.trigger({
				message: error?.message || t("ALERT_MESSAGES.SOMETHING_WENT_WRONG"),
				severity: "error",
			});
		} finally {
			setSubmitting(false);
		}
	};

	const handleCancel = () => {
		setDismissed(true);
	};

	if (!shouldShow) {
		return (
			<ComplianceDescriptionModal
				open={policyModal.open}
				setOpen={(open) =>
					setPolicyModal((prev) => ({ open, doc: open ? prev.doc : null }))
				}
				document={policyModal.doc}
			/>
		);
	}

	return (
		<>
			<Box
				sx={{
					position: "fixed",
					right: { xs: 12, md: 20 },
					bottom: { xs: 12, md: 20 },
					zIndex: 1400,
					maxWidth: { xs: "calc(100vw - 24px)", sm: 420 },
				}}
			>
				<Paper elevation={8} sx={{ p: 2, borderRadius: 2 }}>
					<Stack spacing={1.5}>
						<Typography variant="subtitle1" fontWeight={700}>
							{t("UI_TEXT.COOKIE_NOTICE_TITLE", { defaultValue: "Cookie Notice" })}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{t("UI_TEXT.COOKIE_NOTICE_BODY", {
								defaultValue:
									"We use cookies to improve your experience. Please accept the cookie policy to continue using the portal.",
							})}
						</Typography>
						<Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end">
							<Button variant="text" onClick={handleViewPolicy}>
								{t("UI_TEXT.VIEW_POLICY", { defaultValue: "View Policy" })}
							</Button>
							<Button variant="outlined" color="primary" onClick={handleCancel} disabled={submitting}>
								{t("BUTTON.CANCEL")}
							</Button>
							<Button variant="contained" onClick={handleAccept} disabled={submitting}>
								{submitting
									? t("BUTTON.SAVING", { defaultValue: "Saving..." })
									: t("BUTTON.ACCEPT", { defaultValue: "Accept" })}
							</Button>
						</Stack>
					</Stack>
				</Paper>
			</Box>
			<ComplianceDescriptionModal
				open={policyModal.open}
				setOpen={(open) =>
					setPolicyModal((prev) => ({ open, doc: open ? prev.doc : null }))
				}
				document={policyModal.doc}
			/>
		</>
	);
};

export default CookieConsentBanner;
